import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { getNomenclatures } from 'entities/nomenclatures/api/getNomenclatures';
import { Nomenclature } from 'entities/nomenclatures/model/types';
import { openModal, closeModal } from 'shared/redux/slice/modalSlice';
import { EquipmentSelectorStyled } from './style';
import { Icon } from '../Icons';
import { Input } from '../Input';

interface EquipmentItem {
    nomenclature_id: number;
    count: number;
}

interface EquipmentSelectorProps {
    value?: EquipmentItem[];
    onChange: (value: EquipmentItem[]) => void;
    disabled?: boolean;
    label?: string;
    placeholder?: string;
    required?: boolean;
}

export const EquipmentSelector: React.FC<EquipmentSelectorProps> = ({
    value = [],
    onChange,
    disabled = false,
    label = "Оборудование",
    placeholder = "Выберите оборудование",
    required = false
}) => {
    const dispatch = useDispatch();
    const [selectedEquipment, setSelectedEquipment] = useState<Nomenclature[]>([]);
    const [equipmentCounts, setEquipmentCounts] = useState<{[key: number]: number}>({});
    const [isOpen, setIsOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [allEquipment, setAllEquipment] = useState<Nomenclature[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [selectedQuantity, ] = useState<number>(1);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const { data: equipmentResponse, isLoading } = useQuery({
        queryKey: ['getNomenclatures', currentPage],
        queryFn: () => getNomenclatures(currentPage),
        enabled: (disabled || isOpen),
    });

    useEffect(() => {
        if (equipmentResponse) {
            if (currentPage === 1) {
                setAllEquipment(equipmentResponse);
            } else {
                setAllEquipment(prev => [...prev, ...equipmentResponse]);
            }
            setHasMore(equipmentResponse.length === 10);
        }
    }, [equipmentResponse, currentPage]);

    useEffect(() => {
        if (allEquipment.length > 0 && value.length > 0) {
            const selected = allEquipment.filter(item => value.some(v => v.nomenclature_id === item.id));
            setSelectedEquipment(selected);
            const counts: {[key: number]: number} = {};
            value.forEach(item => {
                counts[item.nomenclature_id] = item.count;
            });
            setEquipmentCounts(counts);
        } else {
            setSelectedEquipment([]);
            setEquipmentCounts({});
        }
    }, [allEquipment, value]);

    const handleEquipmentToggle = (equipment: Nomenclature) => {
        const isSelected = selectedEquipment.some(item => item.id === equipment.id);
        
        if (isSelected) {
            const newSelected = selectedEquipment.filter(item => item.id !== equipment.id);
            setSelectedEquipment(newSelected);

            const newCounts = { ...equipmentCounts };
            delete newCounts[equipment.id];
            setEquipmentCounts(newCounts);
            
            const newValue = newSelected.map(item => ({
                nomenclature_id: item.id,
                count: equipmentCounts[item.id] || 1
            }));
            onChange(newValue);
            setIsOpen(false);
        } else {
            // setSelectedQuantity(1);
            
            dispatch(openModal({
                isOpen: true,
                content: (
                    <div className="equipment-confirmation">
                        <p>Введите количество оборудования, которое хотите добавить</p>
                        <div className="equipment-details">
                            <div className="equipment-info">
                                Оборудование: <strong>{equipment.name}</strong>
                            </div>
                        </div>
                        <div className="quantity-input-container" style={{ marginTop: '20px' }}>
                            <label htmlFor="quantity-input" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                                Количество:
                            </label>
                            <Input
                                id="quantity-input"
                                value={selectedQuantity}
                                onChange={(e: any) => {console.log(e.target.value)}}
                                // maskConfig={{
                                //     mask: Number,
                                //     // min: 1,
                                // }}
                            />
                        </div>
                    </div>
                ),
                config: {
                    title: "Подтверждение выбора оборудования",
                    btns: [
                        {
                            onClick: () => {
                                const newSelected = [...selectedEquipment, equipment];
                                setSelectedEquipment(newSelected);

                                const newCounts = { ...equipmentCounts, [equipment.id]: selectedQuantity };
                                setEquipmentCounts(newCounts);

                                const newValue = newSelected.map(item => ({
                                    nomenclature_id: item.id,
                                    count: newCounts[item.id] || 1
                                }));
                                onChange(newValue);
                                
                                setIsOpen(false);
                                dispatch(closeModal());
                            },
                            title: "Добавить",
                            active: selectedQuantity >= 1
                        },
                        {
                            onClick: () => dispatch(closeModal()),
                            title: "Отмена",
                            active: false
                        }
                    ]
                }
            }));
        }
    };

    const handleRemoveEquipment = (equipmentId: number) => {
        const newSelected = selectedEquipment.filter(item => item.id !== equipmentId);
        setSelectedEquipment(newSelected);

        const newCounts = { ...equipmentCounts };
        delete newCounts[equipmentId];
        setEquipmentCounts(newCounts);

        const newValue = newSelected.map(item => ({
            nomenclature_id: item.id,
            count: newCounts[item.id] || 1
        }));
        onChange(newValue);
    };

    const loadMore = () => {
        if (hasMore && !isLoading) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handleDropdownOpen = () => {
        if (!isOpen) {
            setIsOpen(true);
            setCurrentPage(1);
            setAllEquipment([]);
        } else {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <EquipmentSelectorStyled>
            <div className="label">
                {label}
                {required && <span className="required">*</span>}
            </div>
            
            <div className="selector-container" ref={dropdownRef}>
                <div 
                    className={`selector ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
                    onClick={() => !disabled && handleDropdownOpen()}
                >
                    <div className="selected-items">
                        {selectedEquipment.length > 0 ? (
                            <div className="selected-equipment">
                                {selectedEquipment.map(equipment => (
                                    <div key={equipment.id} className="equipment-tag">
                                        {equipment.nomenclature_photo?.read_url ? (
                                        <img 
                                            src={equipment.nomenclature_photo?.read_url} 
                                            alt={equipment.name}
                                            className="equipment-photo"
                                        />
                                        ) : (
                                            <div className="equipment-photo-placeholder"></div>
                                        )}
                                        <span className="equipment-name">{equipment.name}</span>
                                        <span className="equipment-count">x{equipmentCounts[equipment.id] || 1}</span>
                                        {!disabled && (
                                            <button
                                                type="button"
                                                className="remove-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveEquipment(equipment.id);
                                                }}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <span className="placeholder">{placeholder}</span>
                        )}
                    </div>
                    {!disabled && (
                        <div className="arrow" style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(-90deg)' }}>
                            <Icon name="arrowRight" />
                        </div>
                    )}
                </div>

                {isOpen && !disabled && (
                    <div className="dropdown">
                        {isLoading && currentPage === 1 ? (
                            <div className="loading">Загрузка...</div>
                        ) : (
                            <div className="equipment-list">
                                {allEquipment.map(equipment => {
                                    const isSelected = selectedEquipment.some(item => item.id === equipment.id);
                                    return (
                                        <div
                                            key={equipment.id}
                                            className={`equipment-item ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleEquipmentToggle(equipment)}
                                        >
                                            {equipment.nomenclature_photo?.read_url ? (
                                            <img 
                                                src={equipment.nomenclature_photo?.read_url} 
                                                alt={equipment.name}
                                                className="equipment-photo"
                                            />
                                            ) : (
                                                <div className="equipment-photo-placeholder"></div>
                                            )}
                                            <div className="equipment-info">
                                                <div className="equipment-name">{equipment.name}</div>
                                            </div>
                                            <div className="selection-indicator">
                                                {isSelected ? '✓' : ''}
                                            </div>
                                        </div>
                                    );
                                })}
                                {hasMore && (
                                    <div className="load-more">
                                        <button 
                                            type="button" 
                                            className="load-more-btn"
                                            onClick={loadMore}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Загрузка...' : 'Загрузить еще'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </EquipmentSelectorStyled>
    );
};
